import React, {Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import FacadeFailureLayerInfo from '@core/scripts/components/layers/FacadeFailureLayer.jsx';
import HintWithIcon from 'deprecated-shared-ui/templates/hint/with-icon';
import SearchAlternatives from './SearchAlternatives.jsx';
import AlpiList from './AlpiList.jsx';
import Pagination from './Pagination.jsx';
import Device from '@core/scripts/components/device';
import {articleOverviewStaticContext} from '../ArticleOverviewStatic.jsx';
import {Constants} from '../view-constants';
import Landmark from '@core/scripts/components/landmark-adapter';
import SeoContent from '../footer/SeoContent.jsx';
import BackToTopButton from './BackToTopButton.jsx';
import {ArticleOverviewTeaserContent} from '@teaser/scripts/components/article-overview-teaser-content/ArticleOverviewTeaserContent.jsx';
import {useDispatch, useSelector} from 'react-redux';

import {
    selectContentAjaxCall,
    selectFilterAjaxCall,
    setContentAjaxCall,
    setContentAjaxCallPopstate,
} from '../AjaxCallSlice.jsx';
import JSONParser from '@core/scripts/helper/json-parser';
import useEffectOnUpdate from '../helper/hooks-helper';
import {loadData} from '../helper/data-loader';
import {loadOff, loadOffFilter, loadOn} from '../LoaderSlice.jsx';
import History from '@core/scripts/components/history';
import {createUrlFromCurrentUrl, getContentAjaxUrl, pushUrlToHistory} from '../ArticleOverviewHelper';
import {useTrinity} from '../hooks/use-trinity.js';
import OverviewHeader from '@article-overview/components/header/OverviewHeader.jsx';
import {DefaultFallback} from '@shopping-funnel/scripts/components/fallbacks/DefaultFallback.jsx';
import {useInfiniteArticles} from '@article-overview/components/hooks/use-infinite-articles.js';
import {usePaginatedArticles} from '@article-overview/components/hooks/use-paginated-articles.js';
import {rebuildUrl} from '@article-overview/components/hooks/rebuild-url.js';
import {useBreakpointCurrent, Alert} from 'bonprix-design-system';
import LeftNavigation from './LeftNavigation.jsx';
import {getColumnsForBreakpoint} from '../helper/breakpoint-helper';
import {
    shouldApplyStoredSizeFilter,
    applyStoredSizeFilterToParams,
    clearSizeFilter,
    getShopGroup,
    getStoredSourceShopId,
} from '../../utils/size-filter-persistence.js';
import {updateDyCategoryRecommendationContext} from '@dynamic-yield/scripts/apis/dy-integration-api';

const ArticleOverviewEndOfPageContent = React.lazy(
    () => import('@teaser/scripts/components/article-overview-teaser-content/ArticleOverviewEndOfPageContent.jsx'),
);

const RecommendationContainer = React.lazy(() => import('../footer/RecommendationContainer.jsx'));

function calculateTeaserGridSpace(items, currentBreakpoint) {
    const teaserAlpis = items.filter((alpi) => {
        return alpi.gridItem.displayType === Constants.GRIDITEM_TEASER;
    });

    let totalTeaserSpace = 0;
    const tempBreakpoint = currentBreakpoint.toUpperCase();

    for (const teaser of teaserAlpis) {
        const gridItem = teaser.gridItem;

        const breakpointDimensions = gridItem.breakpointDimensions?.[tempBreakpoint];
        const columns = breakpointDimensions?.columns  || 1 ;
        const rows = breakpointDimensions?.rows || 1;

        totalTeaserSpace += columns * rows;
    }

    return totalTeaserSpace;
}


function trimLastItemsForInfiniteScroll(items, currentBreakpoint, stripLastRowToFit) {
    if (!stripLastRowToFit) {
        return items;
    }

    const colsForCurrentBreakpoint = getColumnsForBreakpoint(currentBreakpoint);

    // Calculate total grid space needed
    const articleCount = items.filter((alpi) =>
        alpi.gridItem.displayType === Constants.GRIDITEM_ARTICLE
    ).length;

    const teaserSpace = calculateTeaserGridSpace(
        items.filter((alpi) => alpi.gridItem.displayType === Constants.GRIDITEM_TEASER),
        currentBreakpoint
    );

    const totalAlpiSize = articleCount + teaserSpace;

    // Trim incomplete last row for infinite scroll
    const overflowCells = totalAlpiSize % colsForCurrentBreakpoint;
    if (overflowCells !== 0) {
        // Walk backwards consuming actual cell sizes (teasers may span multiple cells)
        let cellsToRemove = overflowCells;
        let itemsToRemove = 0;
        const tempBreakpoint = currentBreakpoint.toUpperCase();
        for (let i = items.length - 1; i >= 0 && cellsToRemove > 0; i--) {
            const alpi = items[i];
            if (alpi.gridItem.displayType === Constants.GRIDITEM_TEASER) {
                const dims = alpi.gridItem.breakpointDimensions?.[tempBreakpoint];
                cellsToRemove -= (dims?.columns || 1) * (dims?.rows || 1);
            } else {
                cellsToRemove -= 1;
            }
            itemsToRemove++;
        }
        return items.slice(0, items.length - itemsToRemove);
    }

    return items;
}

export default function ArticleOverviewContent({
    givenArticleContent,
    givenSeoContent,
    givenResponseLimitations,
    givenHeaderContent,
    givenSearchResultContent,
    givenFilterContent,
    givenLandmarkContent,
    givenShopContent,
    givenRecommendationContent,
    givenNavigationContent,
    pointOfSaleId,
    givenTeaserContent,
    givenLeftNavigation,
    givenSearchContext,
    givenConfErrorCase,
    givenRankingTrackingValue,
    completeModel,
}) {
    const {
        texts,
        facadeFailure,
        contentAjaxUrl,
        prettyUrl,
        currentSearchParams,
        shopId,
        filterRootShopId,
        currentUrlPath,
        currentCategoryPath,
        shouldScrollUpAfterLoad,
        loadMoreButtonTestGroup,
        imagePresentationTypeId,
        sizeFilterPersistenceEnabled
    } = useContext(articleOverviewStaticContext);

    const [hidePagination, setHidePagination] = useState(false);
    const [showFilterRemovedAlert, setShowFilterRemovedAlert] = useState(false);
    const [showFilterAppliedAlert, setShowFilterAppliedAlert] = useState(false);
    const [loadOnlyInitialArticles, setLoadOnlyInitialArticles] = useState(
        !currentSearchParams.current.get(Constants.SEARCH_PARAMS_ANCHOR) &&
            !currentSearchParams.current.get(Constants.SEARCH_PARAMS_TEASER_ANCHOR),
    );
    const {sendLoadMoreBlicked} = useTrinity();

    useEffect(() => {
        if (!loadOnlyInitialArticles) return;

        const loadAllArticles = () => {
            setLoadOnlyInitialArticles(false);
        };

        window.addEventListener('scroll', loadAllArticles, {once: true, passive: true});
        window.addEventListener('touchstart', loadAllArticles, {once: true, passive: true});

        return () => {
            window.removeEventListener('scroll', loadAllArticles);
            window.removeEventListener('touchstart', loadAllArticles);
        };
    }, [loadOnlyInitialArticles]);

    const [filterContent, setFilterContent] = useState(givenFilterContent);
    const [articleContent, setArticleContent] = useState(givenArticleContent);
    const [overviewContent, setOverviewContent] = useState({
        navigationContent: givenNavigationContent,
        headerContent: givenHeaderContent,
        landmarkContent: givenLandmarkContent,
        shopContent: givenShopContent,
        searchResultContent: givenSearchResultContent,
        seoContent: givenSeoContent,
        responseLimitations: givenResponseLimitations,
        recommendationContent: givenRecommendationContent,
        teaserContent: givenTeaserContent,
        leftNavigation: givenLeftNavigation,
        errorCase: givenConfErrorCase,
        rankingTrackingValue: givenRankingTrackingValue,
    });
    const navigationContent = overviewContent.navigationContent;
    const headerContent = overviewContent.headerContent;
    const landmarkContent = overviewContent.landmarkContent;
    const shopContent = overviewContent.shopContent;
    const searchResultContent = overviewContent.searchResultContent;
    const seoContent = overviewContent.seoContent;
    const responseLimitations = overviewContent.responseLimitations;
    const recommendationContent = overviewContent.recommendationContent;
    const leftNavigation = overviewContent.leftNavigation;
    const searchContext = givenSearchContext;

    const dispatch = useDispatch();
    const currentBreakpoint = useBreakpointCurrent();

    const filterAjaxCall = useSelector(selectFilterAjaxCall);
    const contentAjaxCall = useSelector(selectContentAjaxCall);
    const {sendFilterView} = useTrinity();

    const contentLoadedCounter = useRef(0);
    const filterContentCopy = useRef(givenFilterContent);

    const initialDataRendered = useRef(false);
    const appliedStoredSizeFilter = useRef(false);
    const keepAlertVisible = useRef(false);

    useEffect(() => {
        if (typeof window !== 'undefined' && sessionStorage.getItem('filterRemovedNotification') === 'true') {
            sessionStorage.removeItem('filterRemovedNotification');
            setShowFilterRemovedAlert(true);
        }
    }, []);

    // Apply stored size filter on mount without reload
    useEffect(() => {
        if (!sizeFilterPersistenceEnabled) return;

        if (shouldApplyStoredSizeFilter(currentSearchParams.current, shopId.current, filterRootShopId.current)) {
            const newParams = applyStoredSizeFilterToParams(currentSearchParams.current, shopId.current, filterRootShopId.current);
            currentSearchParams.current = newParams;

            const formattedParams = `?${newParams.toString()}`;
            const contentAjaxCall = getContentAjaxUrl(currentCategoryPath.current, formattedParams, contentAjaxUrl);

            const currentUrl = new URL(window.location.href);
            currentUrl.search = newParams.toString();
            window.history.replaceState({}, '', currentUrl.toString());

            appliedStoredSizeFilter.current = true;

            dispatch(setContentAjaxCall([contentAjaxCall, false, false]));
        }
    }, []);

    useEffect(() => {
        const hasFilterOrContentChanged = filterAjaxCall?.path || contentAjaxCall?.path;
        if (hasFilterOrContentChanged && !keepAlertVisible.current) {
            setShowFilterRemovedAlert(false);
            setShowFilterAppliedAlert(false);
        }
        keepAlertVisible.current = false;
    }, [filterAjaxCall, contentAjaxCall]);

    useEffect(() => {
        let lastScrollY = window.scrollY;

        const handleScroll = () => {
            const currentScrollY = window.scrollY;

            // Check if scrolling down
            if (currentScrollY > lastScrollY && !hidePagination && loadMoreButtonTestGroup) {
                setHidePagination(true);
            }

            lastScrollY = currentScrollY;
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [hidePagination, loadMoreButtonTestGroup]);

    useEffect(() => {
        sendFastFilterViewEvent();
    }, []);

    useEffect(() => {
        if (contentAjaxCall.sendPageLandmark) {
            sendPageLandmark();
        }
    }, [landmarkContent]);

    useEffectOnUpdate(() => {
        if (filterAjaxCall.path) {
            const newFilterContent = loadData(filterAjaxCall.path);
            return newFilterContent.then((result) => {
                if (result) {
                    dispatch(loadOffFilter());
                    return updateFilterModel(result, filterAjaxCall.isRange);
                }
                dispatch(loadOffFilter());
            });
        } else {
            setFilterContent(filterContentCopy.current);
        }
    }, [filterAjaxCall]);

    const updateAndGetModels = useCallback((content) => {
        let modelJson = content.model;
        if (typeof content.model == 'string') {
            modelJson = JSONParser.tryParseJSON(content.model);
        }

        // If we applied a stored size filter and got 0 hits, clear the filter and refetch
        if (appliedStoredSizeFilter.current && modelJson.filterContent?.hits === 0) {
            appliedStoredSizeFilter.current = false;

            const group = getShopGroup(shopId.current, filterRootShopId.current);
            if (group) {
                clearSizeFilter(group);
            }

            const newParams = new URLSearchParams(currentSearchParams.current);
            const keysToDelete = [];
            for (const key of newParams.keys()) {
                if (key.startsWith('fh_groesse')) {
                    keysToDelete.push(key);
                }
            }
            keysToDelete.forEach(key => newParams.delete(key));
            currentSearchParams.current = newParams;


            const currentUrl = new URL(window.location.href);
            currentUrl.search = newParams.toString();
            window.history.replaceState({}, '', currentUrl.toString());

            keepAlertVisible.current = true;
            setShowFilterRemovedAlert(true);

            const formattedParams = `?${newParams.toString()}`;
            const newContentAjaxCall = getContentAjaxUrl(currentCategoryPath.current, formattedParams, contentAjaxUrl);
            dispatch(setContentAjaxCall([newContentAjaxCall, false, false]));

            return null;
        }

        // If we applied a stored size filter and got hits, show success alert only if shop changed
        if (appliedStoredSizeFilter.current && modelJson.filterContent?.hits > 0) {
            const group = getShopGroup(shopId.current, filterRootShopId.current);
            const sourceShopId = getStoredSourceShopId(group);
            const currentShopId = shopId.current;

            // Only show alert if the shop has changed (not when returning from PDP to same shop)
            const shopHasChanged = sourceShopId && sourceShopId !== currentShopId;

            if (shopHasChanged && !sessionStorage.getItem('sizeFilterAppliedAlertShown')) {
                sessionStorage.setItem('sizeFilterAppliedAlertShown', 'true');
                keepAlertVisible.current = true;
                setShowFilterAppliedAlert(true);
            }
        }

        appliedStoredSizeFilter.current = false;

        const updatedModel = {
            navigationContent: modelJson.navigationContent,
            headerContent: modelJson.headerContent,
            landmarkContent: modelJson.landmarkContent,
            shopContent: modelJson.shopContent,
            searchResultContent: modelJson.searchResultContent,
            seoContent: modelJson.seoContent,
            responseLimitations: modelJson.responseLimitations,
            recommendationContent: modelJson.recommendationContent,
            teaserContent: modelJson.teaserContent,
            leftNavigation: modelJson.leftNavigation,
            errorCase: modelJson.configurationContent.errorCase,
            rankingTrackingValue: modelJson.rankingTrackingValue,
        };
        const articleContent = modelJson.articleContent;
        articleContent.contentLoadedCounter = ++contentLoadedCounter.current;
        setArticleContent(articleContent);
        setFilterContent(modelJson.filterContent);
        filterContentCopy.current = modelJson.filterContent;
        setOverviewContent(updatedModel);

        return modelJson;
    });

    function updateContent(result, popstate = false) {
        const newContent = updateAndGetModels(result);

        if (!newContent) {
            return;
        }

        prettyUrl.current = newContent.shopContent?.prettyUrl;
        shopId.current = newContent.shopContent?.id;
        imagePresentationTypeId.current = newContent.configurationContent?.imagePresentationTypeId;

        // Dynamic Yield reads the page context after the url changed, so the category is published
        // here and the dy integration decides when it reaches Dynamic Yield: right away when the
        // browser already navigated, as on back and forward, and otherwise when the push below
        // arrives.
        updateDyCategoryRecommendationContext(newContent.shopContent?.dyCategoryPath);

        if (popstate) {
            const currentUrl = createUrlFromCurrentUrl(searchContext);
            const sanitizedUrl = newContent.sanitizedUrl;
            const newUrl = sanitizedUrl ? new URL(currentUrl.origin + sanitizedUrl) : new URL(currentUrl);
            //TODO MAKE ONE CALL
            currentCategoryPath.current = prettyUrl.current;
            currentUrlPath.current = newUrl.pathname;
            currentSearchParams.current = newUrl.searchParams;
            return;
        }

        const currentFullUrl = window.location.pathname + window.location.search;
        const newFullUrl = currentUrlPath.current + '?' + currentSearchParams.current.toString();

        const currentRebuilt = rebuildUrl(currentFullUrl);
        const newRebuilt = rebuildUrl(newFullUrl);
        if (newContent.seoOptimizedUrl) {
            if (currentFullUrl !== newContent.seoOptimizedUrl) {
                History.pushURL(newContent.seoOptimizedUrl, '');
            }
        } else if (currentRebuilt !== newRebuilt) {
            pushUrlToHistory('', currentUrlPath.current, null, currentSearchParams.current);
        }
    }

    const initialAjaxUrl = useMemo(() => {
        const currentSearchParamsCopy = new URLSearchParams(currentSearchParams.current);
        currentSearchParams.current = currentSearchParamsCopy;

        const formattedParams = `?${currentSearchParamsCopy.toString()}`;

        return getContentAjaxUrl(prettyUrl.current, formattedParams, contentAjaxUrl);
    }, [currentSearchParams.current, prettyUrl.current]);

    const onContentUpdated = (content) => {
        updateContent(content, contentAjaxCall.popstate);
    };

    // Get current page from URL
    const currentPageFromUrl =
        parseInt(new URLSearchParams(window.location.search).get(Constants.SEARCH_PARAMS_PAGE)) || 1;
    const shouldRenderTeaser = currentPageFromUrl === 1 || loadMoreButtonTestGroup;

    const infiniteQuery = useInfiniteArticles({
        enabled: loadMoreButtonTestGroup,
        initialAjaxUrl: initialAjaxUrl,
        ajaxUrl: contentAjaxCall.path,
        prettyUrl: prettyUrl.current,
        givenSeoOptimizedUrl: overviewContent.seoOptimizedUrl,
        givenSanitizedUrl: overviewContent.sanitizedUrl,
        onContentUpdated: onContentUpdated,
        maxPages: navigationContent.pagination.numberOfPages,
        initialModel: completeModel,
    });

    const paginatedQuery = usePaginatedArticles({
        enabled: !loadMoreButtonTestGroup,
        initialAjaxUrl: initialAjaxUrl,
        ajaxUrl: contentAjaxCall.path,
        currentPage: currentPageFromUrl,
        givenSeoOptimizedUrl: overviewContent.seoOptimizedUrl,
        givenSanitizedUrl: overviewContent.sanitizedUrl,
        onContentUpdated: onContentUpdated,
        initialModel: completeModel,
    });

    // Use useMemo to derive alpiItems directly from React Query data and reorder them
    const alpiItems = useMemo(() => {
        let items = [];
        const bp = currentBreakpoint.toUpperCase();
        const sortByBp = (a, b) => (a.orderings?.[bp] ?? Infinity) - (b.orderings?.[bp] ?? Infinity);
        if (loadMoreButtonTestGroup && infiniteQuery.data) {
            // Sort within each page separately to preserve page boundaries across multiple appended pages
            items = infiniteQuery.data.pages.flatMap((page) => [...page.articles].sort(sortByBp));
        }
        if (!loadMoreButtonTestGroup && paginatedQuery.data) {
            items = paginatedQuery.data.articles;
        }
        // on the first render, return always the passed data for immediate rendering during query load. Workaround for missing SSR
        if (initialDataRendered.current === false) {
            initialDataRendered.current = true;
            items = givenArticleContent.articleListPageItems;
        }

        // Sort by current breakpoint ordering so teasers land at the correct DOM position
        if (!loadMoreButtonTestGroup) {
            items = [...items].sort(sortByBp);
        }
        if (loadOnlyInitialArticles && items) {
            items = items.slice(0, 36);
        }

        // Prepare items for infinite scroll (trim incomplete rows if needed)
        const preparedItems = trimLastItemsForInfiniteScroll(
            items,
            currentBreakpoint,
            infiniteQuery.hasNextPage && loadMoreButtonTestGroup,
        );


        return preparedItems ?? [];
    }, [
        loadMoreButtonTestGroup,
        infiniteQuery.data,
        infiniteQuery.hasNextPage,
        paginatedQuery.data,
        givenArticleContent.articleListPageItems,
        loadOnlyInitialArticles,
        currentBreakpoint,
    ]);

    // Absolute index of the first product the grid renders, which the product-click event adds to a
    // product's position on the page. Derived from the pagination the model already carries and the
    // page size the backend requests (PageParameterStep) — exact on every page, because GridFactory
    // adds teasers on top of the page's products rather than in place of them. In the load-more
    // group the grid holds every page fetched so far, so the offset is the first *loaded* page's —
    // usually 0, but not when the shopper entered on a deep-linked page.
    const firstRenderedPage = loadMoreButtonTestGroup
        ? infiniteQuery.data?.pages?.[0]?.page
        : navigationContent.pagination?.currentPageNumber;
    const firstItemIndex = (Math.max(firstRenderedPage ?? 1, 1) - 1) * Constants.PAGE_SIZE;

    // Calculate current article count for infinite scroll
    const currentArticleCount = useMemo(() => {
        return alpiItems.filter((alpi) => alpi.gridItem.displayType === Constants.GRIDITEM_ARTICLE).length;
    }, [alpiItems]);

    // Handle scroll restoration separately
    useEffect(() => {
        if (shouldScrollUpAfterLoad.current) {
            window.scrollTo({top: 0, behavior: 'instant'});
            shouldScrollUpAfterLoad.current = false;
        }
    }, [alpiItems]);

    // Handle infinite query loading state
    useEffect(() => {
        if (!loadMoreButtonTestGroup) {
            return;
        }
        if (
            infiniteQuery.isFetching ||
            infiniteQuery.isLoading ||
            infiniteQuery.isPlaceholderData ||
            infiniteQuery.isFetchingNextPage
        ) {
            dispatch(loadOn());
        } else {
            dispatch(loadOff());
        }
    }, [
        infiniteQuery.isFetching,
        infiniteQuery.isLoading,
        infiniteQuery.isPlaceholderData,
        infiniteQuery.isFetchingNextPage,
        loadMoreButtonTestGroup,
    ]);

    // Handle paginated query loading state
    useEffect(() => {
        if (loadMoreButtonTestGroup) {
            return;
        }
        if (paginatedQuery.isFetching) {
            dispatch(loadOn());
        } else {
            dispatch(loadOff());
        }
    }, [paginatedQuery.isFetching, loadMoreButtonTestGroup]);

    useEffect(() => {
        window.addEventListener('popstate', () => {
            const category = 'kategorie/';
            const currentUrl = createUrlFromCurrentUrl(searchContext);
            if (currentUrl.hash !== '#menu' && currentUrl.hash !== '#filterLayer') {
                const pathname = currentUrl.pathname;
                const prettyUrlIncludingSeoFilter = pathname.substring(
                    pathname.indexOf(category) + category.length,
                    pathname.length - 1,
                ); //e.g. "/de_de/kategorie/damen-mode-kleider/rot_42/" -> damen-mode-kleider/rot_42

                // TODO: Test
                const call = getContentAjaxUrl(
                    prettyUrl.current ? prettyUrlIncludingSeoFilter : '',
                    currentUrl.search,
                    contentAjaxUrl,
                );

                shouldScrollUpAfterLoad.current = true;
                dispatch(setContentAjaxCallPopstate(call));
            }
        });
        return () => {};
    }, []);

    const updateFilterModel = useCallback(
        (payload, isRangeFilter) => {
            const modelJson = JSONParser.tryParseJSON(payload.model);
            if (!modelJson.hits) {
                // get filter names from filterContentCopy as they are not available if there are no hits
                modelJson.filterGroups.forEach((filterGroup) => {
                    const matchingGroup = filterContentCopy.current.filterGroups.find(
                        (copyGroup) => copyGroup.identifier === filterGroup.identifier,
                    );
                    if (matchingGroup) {
                        filterGroup.name = matchingGroup.name;
                    }
                });
            }
            if (!modelJson.hits && isRangeFilter) {
                return;
            }
            setFilterContent(modelJson);

            return modelJson.hits;
        },
        [setFilterContent],
    );

    const isApp = Device.isApp();
    // The app has no browser chrome above the page, so the content needs its own offset from the
    // native app header. Together with the category slider's own 8px it makes up the 24px the app
    // design asks for above the slider, which is the first element on a category page in the app.
    const articleOverviewClasses = `article-overview__wrapper${isApp ? ' mt-4' : ''}`;
    const filledArticleList = alpiItems.length > 0;
    const noArticlesError = overviewContent.errorCase === 'NO_ARTICLES';
    const responseLimitationHint =
        responseLimitations?.includes(Constants.RESPONSE_LIMITATION_INACCURATE_PRODUCTS) ||
        responseLimitations?.includes(Constants.RESPONSE_LIMITATION_INACCURATE_PRODUCTS_SELECTED);

    const shouldRenderBreadcrumbs =
        headerContent.breadcrumbs && ((shopContent && !isApp) || searchResultContent);
    const onLoadMoreClick = async () => {
        if (loadMoreButtonTestGroup) {
            // Save current scroll position
            const scrollPosition = window.scrollY;
            const currentSearchParamsCopy = new URLSearchParams(currentSearchParams.current);
            const currentPage = parseInt(currentSearchParamsCopy.get(Constants.SEARCH_PARAMS_PAGE) ?? 1);
            currentSearchParamsCopy.set(Constants.SEARCH_PARAMS_PAGE, currentPage + 1);
            currentSearchParams.current = currentSearchParamsCopy;

            const formattedParams = `?${currentSearchParamsCopy.toString()}`;
            const contentAjaxCall = getContentAjaxUrl(currentCategoryPath.current, formattedParams, contentAjaxUrl);

            dispatch(setContentAjaxCall([contentAjaxCall, false, false]));
            const result = await infiniteQuery.fetchNextPage();
            sendLoadMoreBlicked(result.data.pages.flatMap((item) => item.articles).length);
            if (result.data) {
                const lastPage = result.data.pages[result.data.pages.length - 1];
                if (lastPage?.page) {
                    // Use replaceState with scroll restoration disabled
                    history.replaceState({}, '', window.location.pathname + '?' + currentSearchParamsCopy.toString());
                }
            }
        }
    };

    return (
        <>
            {overviewContent.errorCase !== 'NO_ERROR' && (
                <FacadeFailureLayerInfo
                    key={`article-overview__facade-failure_${new Date().getTime()}`}
                    texts={facadeFailure}
                />
            )}

            {responseLimitationHint && (
                <HintWithIcon className="article-overview__response-limitation-hint" icon="hint">
                    {texts.responseLimitation}
                </HintWithIcon>
            )}

            <div className={articleOverviewClasses}>
                <div className="article-overview">
                    <OverviewHeader
                        shouldRenderBreadcrumbs={shouldRenderBreadcrumbs}
                        breadcrumbs={headerContent.breadcrumbs}
                        hideBreadcrumbBack={completeModel?.hideBreadcrumbBack || false}
                        landmarkContent={landmarkContent}
                        filterContent={filterContent}
                        ordering={articleContent.ordering}
                        filledArticleList={filledArticleList}
                        noArticlesError={noArticlesError}
                    />

                    <div className={`article-overview__content-layout${leftNavigation ? ' -has-left-navigation' : ''}`}>
                        {leftNavigation && <LeftNavigation onsiteNavigation={leftNavigation} />}

                        <div className="article-overview__content-main">
                            {shouldRenderTeaser && overviewContent.teaserContent?.content?.length > 0 && (
                                <div className="article-overview__teaser-content">
                                    <ArticleOverviewTeaserContent model={overviewContent.teaserContent} />
                                </div>
                            )}

                            {headerContent.hintText && (
                                <HintWithIcon className="article-overview__hint-headline" icon="hint">
                                    <span className="hint__message-bold">{headerContent.hintText.headline}</span>
                                    <div className="article-overview__hint-message">{headerContent.hintText.message}</div>
                                </HintWithIcon>
                            )}

                            {searchResultContent?.searchAlternatives && (
                                <SearchAlternatives
                                    searchAlternatives={searchResultContent.searchAlternatives}
                                    searchQuery={searchResultContent.searchQuery}
                                />
                            )}

                            <div className="article-overview__sticky-article-filter-wrapper">
                                {showFilterRemovedAlert && (
                                    <div className="flex items-center px-2.5 py-3 xl:px-11">
                                        <Alert
                                            isOpen={true}
                                            status={'info'}
                                            variant={'secondary'}
                                            showCloseIcon={false}
                                            title={texts.filter_removedByUnavailability}
                                        ></Alert>
                                    </div>
                                )}
                                {showFilterAppliedAlert && (
                                    <div className="flex items-center px-2.5 py-3 xl:px-11">
                                        <Alert
                                            isOpen={true}
                                            status={'info'}
                                            variant={'secondary'}
                                            showCloseIcon={false}
                                            title="Wir haben diesen Größen-Filter aufgrund deiner letzten Auswahl automatisch übernommen."
                                        >
                                        </Alert>
                                    </div>
                                )}
                                {/*TODO: TOO MANY RENDERS FIXEN UND KEY??*/}
                                {filledArticleList ? (
                                    <DefaultFallback>
                                        <AlpiList
                                            alpiList={alpiItems}
                                            shopContent={shopContent}
                                            filterTrackingValue={filterContent.filterTrackingValue}
                                            rankingTrackingValue={overviewContent.rankingTrackingValue}
                                            pointOfSaleId={pointOfSaleId}
                                            contentLoadedCounter={articleContent.contentLoadedCounter}
                                            teaserContent={overviewContent.teaserContent}
                                            onLoadMore={onLoadMoreClick}
                                            isLoadingMore={loadMoreButtonTestGroup ? infiniteQuery.isFetchingNextPage : false}
                                            hasMorePages={loadMoreButtonTestGroup ? infiniteQuery.hasNextPage : false}
                                            loadMoreButtonTestGroup={loadMoreButtonTestGroup}
                                            hits={headerContent.breadcrumbs.hits}
                                            currentArticles={currentArticleCount}
                                            filterTokens={filterContent.filterTokens}
                                            searchQuery={searchResultContent?.searchQuery}
                                            firstItemIndex={firstItemIndex}
                                        />
                                    </DefaultFallback>
                                ) : noArticlesError ? (
                                    <></>
                                ) : (
                                    <HintWithIcon className="alpi-list__empty-hint" icon="hint">
                                        <span className="hint__message-bold">{texts.filter_noHitsPart1}</span>
                                        {texts.filter_noHitsPart2}
                                    </HintWithIcon>
                                )}
                            </div>
                        </div>
                    </div>

                    {navigationContent.pagination.paginationItems.length > 0 && !hidePagination && (
                        <Pagination pagination={navigationContent.pagination} isShopContent={shopContent !== null} />
                    )}

                    {shouldRenderTeaser &&
                        overviewContent.teaserContent &&
                        overviewContent.teaserContent?.endOfPageContent?.length > 0 && (
                            <div className="article-overview__teaser-end-of-page-content">
                                <Suspense fallback={<></>}>
                                    <ArticleOverviewEndOfPageContent model={overviewContent.teaserContent} />
                                </Suspense>
                            </div>
                        )}
                </div>
            </div>

            <BackToTopButton />

            {recommendationContent && (
                <Suspense fallback={<div></div>}>
                    <RecommendationContainer
                        key={recommendationContent.recommendationData}
                        texts={texts}
                        ajaxSwiperData={recommendationContent.recommendationData}
                        page={!shopId.current ? 'search' : 'aop'}
                    />
                </Suspense>
            )}

            <SeoContent seoContent={seoContent} />
        </>
    );

    function sendPageLandmark() {
        if (landmarkContent?.pageLandmark) {
            Landmark.sendPageLandmark(JSON.parse(landmarkContent.pageLandmark));
        }
    }

    function sendFastFilterViewEvent() {
        if (filterContent?.shopIdFilterGroup) {
            const shopIdFilterGroupTrackingId = filterContent.shopIdFilterGroup.filters[0]?.trackingId;
            sendFilterView('fast_filter', `${shopIdFilterGroupTrackingId}|`, Constants.FILTER_VIEW_EVENT_VERSION);
        }
    }
}
